import { environment } from 'src/environments/environment';

/**
 * Maps a network's community webpage settings (space_* fields, font_family,
 * fs_*, card_shape) to the --cm-* CSS variables used by the v2 community page.
 * Shades for borders, hover, muted text and raised cards are derived from each
 * area's own background and text colour, so admins need no extra fields.
 * Text colours below 4.5:1 contrast are replaced by black or white.
 */

type Rgb = [number, number, number];

const WHITE: Rgb = [255, 255, 255];
const NEAR_BLACK: Rgb = [17, 17, 17];
const TEXT_CONTRAST = 4.5;
const ACCENT_CONTRAST = 3;
/** Favourites read as gold everywhere; network accents can be grey or white, which hides the starred state. */
const STAR_COLOR = '#f5b301';

/** Same defaults channel-webpage.guard merges when a network has no Community colours. */
const DEFAULTS: Record<string, string> = (environment as any).communityDefaultColors || {};

export function parseHex(value: string): Rgb | null {
  if (typeof value !== 'string') return null;
  let h = value.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length === 8) h = h.slice(0, 6);
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [0, 2, 4].map(i => parseInt(h.substr(i, 2), 16)) as Rgb;
}

function toHex(c: Rgb): string {
  return '#' + c.map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}

export function luminance([r, g, b]: Rgb): number {
  const f = (c: number) => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const x = luminance(a);
  const y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return a.map((v, i) => v + (b[i] - v) * t) as Rgb;
}

/** Keeps the admin's text colour when readable, else the better of black / white. */
export function readableOn(fg: string, bg: string): { color: string; corrected: boolean } {
  const f = parseHex(fg);
  const b = parseHex(bg);
  if (!b) return { color: fg, corrected: false };
  if (f && contrastRatio(f, b) >= TEXT_CONTRAST) return { color: toHex(f), corrected: false };
  const color = contrastRatio(WHITE, b) >= contrastRatio(NEAR_BLACK, b) ? '#ffffff' : '#111111';
  return { color, corrected: true };
}

/** Moves an accent toward the text colour until it reaches 3:1 on the background. */
export function accentOn(accent: string, bg: string, fg: string): string {
  const a = parseHex(accent);
  const b = parseHex(bg);
  const f = parseHex(fg);
  if (!a || !b || !f) return accent;
  let out = a;
  for (let k = 1; contrastRatio(out, b) < ACCENT_CONTRAST && k <= 20; k++) {
    out = mix(a, f, k * 0.05);
  }
  return toHex(out);
}

/** Last resort for tenants whose environment has no communityDefaultColors. */
const NEUTRAL_BG = '#1e2328';
const NEUTRAL_FG = '#ffffff';

const isHex = (v: any): v is string => typeof v === 'string' && !!parseHex(v);

/** Network setting, then the given fallback, then the environment default, then neutral. */
function pick(settings: any, key: string, fallback?: string): string {
  const candidates = [settings?.[key], fallback, DEFAULTS[key]];
  return candidates.find(isHex) ?? (key.includes('font') ? NEUTRAL_FG : NEUTRAL_BG);
}

function px(value: any, fallback: string): string {
  if (value === undefined || value === null || value === '') return fallback;
  return /^\d+(\.\d+)?$/.test(String(value)) ? `${value}px` : String(value);
}

/** Raised surface, border and muted-text shades for one background/text pair. */
function shades(bg: string, fg: string, prefix: string, raise: number, line: number, muted: number) {
  const b = parseHex(bg) as Rgb;
  const f = parseHex(fg) as Rgb;
  return {
    [`--cm-${prefix}raise`]: toHex(mix(b, f, raise)),
    [`--cm-${prefix}line`]: toHex(mix(b, f, line)),
    [`--cm-${prefix}muted`]: toHex(mix(f, b, muted)),
  };
}

export interface CommunityTheme {
  vars: Record<string, string>;
  /** settings keys whose text colour was replaced for readability */
  corrected: string[];
}

export function buildCommunityTheme(settings: any): CommunityTheme {
  const corrected: string[] = [];
  const text = (fgKey: string, bg: string, fgFallback?: string) => {
    const r = readableOn(pick(settings, fgKey, fgFallback), bg);
    if (r.corrected) corrected.push(fgKey);
    return r.color;
  };

  const mainBg = pick(settings, 'space_main_bg_color');
  const navBg = pick(settings, 'space_bg_primary_color');
  const secBg = pick(settings, 'space_bg_secondary_color');
  const btn1Bg = pick(settings, 'space_button_primary_color');
  const btn2Bg = pick(settings, 'space_button_secondary_color');

  const mainFg = text('space_font_main_color', mainBg);
  const navFg = text('space_font_primary_color', navBg);
  const secFg = text('space_font_secondary_color', secBg);
  const btn1Fg = text('space_button_font_primary_color', btn1Bg);
  const btn2Fg = text('space_button_font_secondary_color', btn2Bg);

  const acc = parseHex(btn2Bg) as Rgb;
  const lightMain = luminance(parseHex(mainBg) as Rgb) > 0.5;

  const vars: Record<string, string> = {
    '--cm-main-bg': mainBg,
    '--cm-main-fg': mainFg,
    ...shades(mainBg, mainFg, '', 0.06, 0.14, 0.38),
    '--cm-nav-bg': navBg,
    '--cm-nav-fg': navFg,
    ...shades(navBg, navFg, 'nav-', 0.09, 0.16, 0.35),
    '--cm-sec-bg': secBg,
    '--cm-sec-fg': secFg,
    '--cm-btn1-bg': btn1Bg,
    '--cm-btn1-fg': btn1Fg,
    '--cm-btn2-bg': btn2Bg,
    '--cm-btn2-fg': btn2Fg,
    '--cm-accent': accentOn(btn2Bg, mainBg, mainFg),
    '--cm-nav-accent': accentOn(btn2Bg, navBg, navFg),
    '--cm-star': accentOn(STAR_COLOR, mainBg, mainFg),
    '--cm-tint': `rgba(${acc.join(',')},${lightMain ? 0.12 : 0.2})`,
    '--cm-font': settings?.font_family || "'Roboto', sans-serif",
    '--cm-fs-title': px(settings?.fs_main_title, '20px'),
    '--cm-fs-sub': px(settings?.fs_sub_title, '16px'),
    '--cm-fs-body': px(settings?.fs_sentence, '14px'),
    '--cm-shape': settings?.card_shape === 'square' ? '8px' : '50%',
  };
  return { vars, corrected };
}
